import { useEffect, useState } from "react";
import { useApp } from "../../contexts/AppContext";

// Commit SHA of THIS frontend build, injected at build time
// (VITE_GIT_SHA <- deploy.yml / release.yml). Empty in local dev.
const FRONTEND_SHA = import.meta.env?.VITE_GIT_SHA ?? "dev";
const API_BASE = import.meta.env?.VITE_API_URL ?? "/api";

const short = (s) =>
  s && s !== "dev" && s !== "unknown" ? s.slice(0, 7) : s || "dev";

// Admin-only build-version strip pinned to the bottom of the app shell. Shows the
// commit SHA of the deployed frontend and API so an admin can confirm a release
// went fully live — and spot a frontend/API version mismatch. Renders nothing for
// non-admins (the endpoints stay reachable, the UI is just hidden).
export default function Footer() {
  const { currentUserType } = useApp();
  const [apiSha, setApiSha] = useState(null);

  const isAdmin = currentUserType === "admin";

  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    fetch(`${API_BASE}/version`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => alive && setApiSha(d?.sha ?? "unknown"))
      .catch(() => alive && setApiSha("unreachable"));
    return () => {
      alive = false;
    };
  }, [isAdmin]);

  if (!isAdmin) return null;

  const mismatch =
    apiSha && apiSha !== "unreachable" && short(apiSha) !== short(FRONTEND_SHA);

  return (
    <footer className="flex shrink-0 items-center gap-4 border-t border-(--border-subtle) bg-(--surface-1) px-[30px] py-2 text-[11px] text-(--text-muted)">
      <span>
        frontend{" "}
        <code className="font-mono text-(--text-primary)">
          {short(FRONTEND_SHA)}
        </code>
      </span>
      <span>
        api{" "}
        <code className="font-mono text-(--text-primary)">
          {apiSha ? short(apiSha) : "…"}
        </code>
      </span>
      {mismatch ? (
        <span
          className="font-semibold text-amber-600"
          title="Frontend and API are running different builds"
        >
          ⚠ version mismatch
        </span>
      ) : null}
    </footer>
  );
}
